(() => {
  const geometry = ({headTop, tableBottom, viewportBottom, headerHeight, offset, left, width, windowWidth}) => {
    const bottom = Math.min(tableBottom, viewportBottom);
    return {visible: width > 0 && left < windowWidth && left + width > 0 && headTop < offset - .5 && bottom > offset,
      top: Math.min(offset, bottom - headerHeight), left, width};
  };
  if (typeof module !== 'undefined') module.exports = geometry;
  if (typeof document === 'undefined') return;
  const states = new Map();
  let queued = false;
  const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
  const observer = new ResizeObserver(() => { for (const state of states.values()) state.dirty = true; schedule(); });
  const copyStyle = (source, target) => {
    const computed = getComputedStyle(source);
    for (const property of computed) target.style.setProperty(property, computed.getPropertyValue(property));
  };
  const viewportFor = table => {
    for (let node = table.parentElement; node && node !== document.body; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (/(auto|scroll|hidden)/.test(style.overflowX)) return node;
    }
    return table.parentElement;
  };
  function build(state) {
    const head = state.table.tHead;
    if (!head || !head.rows.length) return false;
    const clone = document.createElement('table');
    copyStyle(state.table, clone);
    clone.style.margin = '0'; clone.style.position = 'relative'; clone.style.tableLayout = 'fixed';
    clone.style.height = 'auto'; clone.style.minHeight = '0'; clone.style.maxHeight = 'none';
    const clonedHead = head.cloneNode(true);
    clonedHead.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
    clonedHead.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
    clone.append(clonedHead);
    const originals = [...head.querySelectorAll('th,td')];
    const copies = [...clonedHead.querySelectorAll('th,td')];
    const measuredRow = [...state.table.rows].filter(row => row.getClientRects().length && [...row.cells].every(cell => cell.colSpan === 1))
      .sort((a, b) => b.cells.length - a.cells.length)[0] ?? head.rows[head.rows.length - 1];
    const widths = [...measuredRow.cells].map(cell => cell.getBoundingClientRect().width);
    const group = document.createElement('colgroup');
    for (const width of widths) { const col = document.createElement('col'); col.style.width = width + 'px'; group.append(col); }
    clone.prepend(group);
    originals.forEach((cell, index) => {
      const copy = copies[index]; copyStyle(cell, copy);
      copy.style.position = 'relative'; copy.style.top = 'auto'; copy.style.left = 'auto'; copy.style.transform = 'none';
      const width = cell.getBoundingClientRect().width;
      copy.style.width = width + 'px'; copy.style.minWidth = width + 'px'; copy.style.maxWidth = width + 'px';
      copy.style.boxSizing = 'border-box';
      if (getComputedStyle(cell).position === 'sticky' && getComputedStyle(cell).left !== 'auto') copy.dataset.fixedColumn = 'true';
    });
    clone.style.width = state.table.getBoundingClientRect().width + 'px';
    clone.style.minWidth = clone.style.width; clone.style.maxWidth = 'none';
    state.overlay.replaceChildren(clone); state.clone = clone; state.head = head; state.dirty = false;
    return true;
  }
  function discover() {
    document.querySelectorAll('main table,.storefront-content table,.tt-table-scroll table').forEach(table => {
      if (table.closest('[data-floating-table-header]') || states.has(table) || !table.tHead) return;
      const overlay = document.createElement('div');
      overlay.dataset.floatingTableHeader = ''; overlay.setAttribute('aria-hidden', 'true'); overlay.inert = true;
      Object.assign(overlay.style, {position:'fixed', overflow:'hidden', pointerEvents:'none', zIndex:'40', display:'none', background:'#f5f5f7'});
      document.body.append(overlay);
      const viewport = viewportFor(table), state = {table, viewport, overlay, dirty:true};
      states.set(table, state); observer.observe(table); observer.observe(viewport); observer.observe(table.tHead);
    });
    schedule();
  }
  function update() {
    queued = false;
    const siteHeader = document.querySelector('#site-header');
    const headerRect = siteHeader?.getBoundingClientRect();
    const offset = headerRect && headerRect.top <= 0 && headerRect.bottom > 0 ? headerRect.bottom : 0;
    for (const [table, state] of states) {
      if (!table.isConnected) { observer.unobserve(table); observer.unobserve(state.viewport); state.overlay.remove(); states.delete(table); continue; }
      if (!table.getClientRects().length || !table.tHead) { state.overlay.style.display = 'none'; continue; }
      if ((state.dirty || state.head !== table.tHead) && !build(state)) continue;
      const rect = table.getBoundingClientRect(), head = table.tHead.getBoundingClientRect(), viewport = state.viewport.getBoundingClientRect();
      const visibleHeadTop = table.tHead.rows[0]?.cells[0]?.getBoundingClientRect().top ?? head.top;
      const result = geometry({headTop:visibleHeadTop, tableBottom:rect.bottom, viewportBottom:viewport.bottom, headerHeight:head.height,
        offset, left:viewport.left + state.viewport.clientLeft, width:state.viewport.clientWidth, windowWidth:innerWidth});
      state.overlay.style.display = result.visible ? 'block' : 'none';
      if (!result.visible) continue;
      Object.assign(state.overlay.style, {top:result.top+'px', left:result.left+'px', width:result.width+'px', height:head.height+'px'});
      const x = state.viewport.scrollLeft;
      state.clone.style.transform = `translateX(${-x}px)`;
      state.clone.querySelectorAll('[data-fixed-column]').forEach(cell => {
        cell.style.transform = `translateX(${x}px)`; cell.style.zIndex = '3';
      });
    }
  }
  const changes = new MutationObserver(records => {
    if (records.every(record => record.target.closest?.('[data-floating-table-header]'))) return;
    for (const state of states.values()) if (records.some(record => state.table.tHead?.contains(record.target))) state.dirty = true;
    discover();
  });
  changes.observe(document.body, {childList:true, subtree:true});
  addEventListener('scroll', schedule, {passive:true, capture:true});
  addEventListener('resize', () => { for (const state of states.values()) state.dirty = true; schedule(); }, {passive:true});
  const header = document.querySelector('#site-header'); if (header) observer.observe(header);
  document.fonts?.ready.then(() => { for (const state of states.values()) state.dirty = true; schedule(); });
  discover();
})();
